import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eyebrow } from './UI';
import { AutoVideo } from './AutoVideo';
import { Crosshair, Target, Users, Ghost, Video, Trophy, ArrowUpRight } from 'lucide-react';
export const UseCases = () => {
  const cases = [{
    num: '01',
    Icon: Crosshair,
    title: 'AIRSOFT WARFARE',
    tag: 'BALLISTIC PROTECTION',
    body: "Full-face BB protection at up to 380 FPS. Anti-fog polycarbonate lenses rated ANSI Z87.1. Maximum peripheral vision. Zero blind spots. The field will know you're serious.",
    videoSrc: 'https://cdn.magicpatterns.com/uploads/soNRBxuFmUQtqQHyKwkyRa/hf_20260513_143208_48f292b4-b3e8-4b62-9051-d6b3a410fa4e.mp4'
  }, {
    num: '02',
    Icon: Target,
    title: 'TACTICAL TRAINING',
    tag: 'FORCE-ON-FORCE',
    body: 'Force-on-force simulation. High-intensity training. Low-profile cheek clearance designed for a proper stock weld. Built to work with your training — not against it.',
    videoSrc: 'https://cdn.magicpatterns.com/uploads/uiZB8yWemuKHxxi4XU21Qy/hf_20260506_145717_f97e664f-a509-4158-bb00-16e1ceb50cdc.mp4'
  }, {
    num: '03',
    Icon: Users,
    title: 'COSPLAY & CONVENTIONS',
    tag: 'ALL-DAY WEAR',
    body: 'Six interchangeable lens colors. All-day wearability. Adjustable reinforced nylon strap fits every head. Walk the floor. Win every room. No competition.',
    videoSrc: 'https://cdn.magicpatterns.com/uploads/hCMgw1VwAQHKDdARCcUpEt/Video Project.mp4'
  }, {
    num: '04',
    Icon: Ghost,
    title: 'EVENTS & HALLOWEEN',
    tag: 'INDESTRUCTIBLE',
    body: 'The only event mask that can survive being run over. While other costumes fall apart by 10pm — yours still looks like it came off a film set.',
    videoSrc: 'https://cdn.magicpatterns.com/uploads/pBQYjrFrdAm7d6jUJBXDfR/hf_20260513_143108_c8bc823e-846b-4da6-9ee7-045827c1f991.mp4'
  }, {
    num: '05',
    Icon: Video,
    title: 'STREAMING & CONTENT',
    tag: 'ON-CAMERA PRESENCE',
    body: "Command your audience's attention instantly. The mirrored lenses hide your eyes while catching ring lights perfectly, creating an intimidating, anonymous persona that keeps viewers locked in. No glare, no fog, just pure aesthetic dominance on stream.",
    videoSrc: 'https://cdn.magicpatterns.com/uploads/2NaWVthi5jQ7uTzZ8aa2sP/hf_20260513_155130_e12c5ae2-534d-4bfc-8687-94f15e654536.mp4'
  }, {
    num: '06',
    Icon: Trophy,
    title: 'COMPETITION & SPORT',
    tag: 'SQUAD GRADE',
    body: 'Paintball. Laser tag. Battle simulation. When your team looks like a unit — they fight like one. The BLACKOUT UNIT Ultimate Mask makes the whole squad elite.'
  }];
  const [active, setActive] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resumeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Briefly pause on user interaction, then auto-resume after 6s
  const pauseTemporarily = () => {
    setIsPaused(true);
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    resumeTimeoutRef.current = setTimeout(() => {
      setIsPaused(false);
    }, 6000);
  };
  const current = cases[active];
  // Auto-rotate carousel on mobile
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setActive(prev => (prev + 1) % cases.length);
    }, 4500);
    return () => clearInterval(interval);
  }, [isPaused, cases.length]);
  // Sync carousel scroll position when active changes — ONLY during auto-play
  useEffect(() => {
    if (isPaused) return;
    if (carouselRef.current && typeof window !== 'undefined' && window.innerWidth < 1024) {
      const container = carouselRef.current;
      const cardWidth = container.scrollWidth / cases.length;
      container.scrollTo({
        left: cardWidth * active,
        behavior: 'smooth'
      });
    }
  }, [active, cases.length, isPaused]);
  // Debounced scroll handler — only updates the active dot AFTER user stops scrolling
  // This prevents fighting between React state updates and browser scroll-snap behavior
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => {
      const cardWidth = container.scrollWidth / cases.length;
      const newActive = Math.round(container.scrollLeft / cardWidth);
      if (newActive !== active && newActive >= 0 && newActive < cases.length) {
        setActive(newActive);
      }
    }, 120);
  };
  const renderVideoPanel = (caseData: (typeof cases)[0], index: number) => <div className="relative h-full lg:min-h-[600px] bg-base-black border border-dark-3 overflow-hidden" data-id="element-1491">
      {/* Cinematic Video Background */}
      <div className="absolute inset-0 z-0" data-id="element-1492">
        <AnimatePresence mode="wait" data-id="element-1493">
          <motion.video key={caseData.videoSrc || 'default-video'} ref={(el: HTMLVideoElement | null) => {
          if (el) {
            el.muted = true;
            el.defaultMuted = true;
            const p = el.play();
            if (p && typeof p.catch === 'function') p.catch(() => {});
          }
        }} src={caseData.videoSrc || 'https://cdn.magicpatterns.com/uploads/hoqbokUhsfYRknubxk99SE/hf_20260513_155658_06e7bfdc-782a-4874-be5d-bd4bb277010e.mp4'} initial={{
          opacity: 0
        }} animate={{
          opacity: 0.8
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.5
        }} autoPlay loop muted playsInline preload="auto" onCanPlay={e => {
          const p = e.currentTarget.play();
          if (p && typeof p.catch === 'function') p.catch(() => {});
        }} className="absolute inset-0 w-full h-full object-cover" data-id="element-1494" />
        </AnimatePresence>
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-base-black/60 via-base-black/30 to-transparent" data-id="element-1495"></div>
      </div>

      {/* Tactical grid overlay */}
      <div className="absolute inset-0 bg-tactical-grid opacity-30 pointer-events-none z-0" data-id="element-1496"></div>

      {/* Massive number watermark */}
      <AnimatePresence mode="wait" data-id="element-1497">
        <motion.span key={`num-${index}`} initial={{
        opacity: 0,
        scale: 1.1
      }} animate={{
        opacity: 0.06,
        scale: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.4
      }} className="absolute -right-8 -bottom-16 font-bebas text-[260px] md:text-[360px] leading-none text-acid-green pointer-events-none select-none" data-id="element-1498">
          {caseData.num}
        </motion.span>
      </AnimatePresence>

      {/* Corner brackets */}
      <div className="absolute top-0 left-0 w-10 h-10 border-t-2 border-l-2 border-acid-green/40 pointer-events-none" data-id="element-1499"></div>
      <div className="absolute top-0 right-0 w-10 h-10 border-t-2 border-r-2 border-acid-green/40 pointer-events-none" data-id="element-1500"></div>
      <div className="absolute bottom-0 left-0 w-10 h-10 border-b-2 border-l-2 border-acid-green/40 pointer-events-none" data-id="element-1501"></div>
      <div className="absolute bottom-0 right-0 w-10 h-10 border-b-2 border-r-2 border-acid-green/40 pointer-events-none" data-id="element-1502"></div>

      {/* Content */}
      <div className="relative z-10 p-6 md:p-12 h-full flex flex-col justify-end lg:justify-start" data-id="element-1503">
        <AnimatePresence mode="wait" data-id="element-1504">
          <motion.div key={`content-${index}`} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.35,
          ease: 'easeOut'
        }} className="flex-1 flex flex-col" data-id="element-1505">
            {/* Tag */}
            <div className="inline-flex items-center gap-2 self-start border border-acid-green/30 bg-acid-green/5 px-2.5 py-1 mb-4 lg:mb-6" data-id="element-1506">
              <span className="w-1 h-1 bg-acid-green animate-pulse" data-id="element-1507"></span>
              <span className="font-barlow font-bold text-[10px] text-acid-green tracking-[3px] uppercase" data-id="element-1508">
                {caseData.tag}
              </span>
            </div>

            <div className="hidden lg:block" data-id="element-1509">
              {/* Icon */}
              <div className="text-acid-green mb-6" data-id="element-1510">
                <caseData.Icon size={42} strokeWidth={1.5} data-id="element-1511" />
              </div>

              {/* Title */}
              <h3 className="font-bebas text-4xl md:text-5xl lg:text-6xl leading-[0.9] tracking-tight mb-6 text-white drop-shadow-lg" data-id="element-1512">
                {caseData.title}
              </h3>

              {/* Body */}
              <p className="font-barlow text-lg text-white/90 leading-[1.7] max-w-xl text-justify hyphens-auto drop-shadow-md font-medium" data-id="element-1513">
                {caseData.body}
              </p>
            </div>

            {/* Footer with progress */}
            <div className="mt-auto pt-4 lg:pt-8 flex items-center justify-between" data-id="element-1514">
              <div className="font-barlow font-bold text-[10px] text-text-dimmed tracking-[3px] uppercase" data-id="element-1515">
                Mission {caseData.num} / 06
              </div>
              <div className="flex gap-1.5" data-id="element-1516">
                {cases.map((_, i) => <button key={i} onClick={() => setActive(i)} aria-label={`Go to mission ${i + 1}`} className={`h-[2px] transition-all duration-300 ${i === index ? 'w-8 bg-acid-green' : 'w-4 bg-dark-3 hover:bg-text-dimmed'}`} data-id="element-1517" />)}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
  return <section className="bg-[#0a0a0a] pt-[60px] pb-[40px] overflow-hidden" data-id="element-1518">
      <div className="container mx-auto px-6 md:px-12" data-id="element-1519">
        <div className="text-center mb-14" data-id="element-1520">
          <Eyebrow data-id="element-1521">MISSION VERSATILITY</Eyebrow>
          <h2 className="text-5xl md:text-[72px] leading-[0.9] mb-4" data-id="element-1522">
            ONE MASK. EVERY THEATER.
          </h2>
          <p className="font-barlow text-lg text-text-body" data-id="element-1523">
            We designed it once. It deploys everywhere.
          </p>
        </div>

        {/* MOBILE: Immersive Horizontal Video Carousel */}
        <div className="lg:hidden -mx-6" data-id="element-1524">
          <div ref={carouselRef} className="flex overflow-x-auto snap-x snap-mandatory gap-3 px-6 pb-2" style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch'
        }} onTouchStart={pauseTemporarily} onScroll={handleScroll} data-id="element-1525">
            {cases.map((c, i) => <div key={i} className="snap-center shrink-0 w-[85vw] h-[70vh] relative bg-base-black border border-dark-3 overflow-hidden" data-id="element-1526">
                {/* Full Video Background */}
                <AutoVideo src={c.videoSrc || 'https://cdn.magicpatterns.com/uploads/hoqbokUhsfYRknubxk99SE/hf_20260513_155658_06e7bfdc-782a-4874-be5d-bd4bb277010e.mp4'} className="absolute inset-0 w-full h-full object-cover opacity-80" data-id="element-1527" />

                {/* Gradient overlay for text readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-base-black via-base-black/50 to-transparent" data-id="element-1528"></div>

                {/* Tactical grid overlay */}
                <div className="absolute inset-0 bg-tactical-grid opacity-20 pointer-events-none" data-id="element-1529"></div>

                {/* Corner brackets */}
                <div className="absolute top-3 left-3 w-8 h-8 border-t-2 border-l-2 border-acid-green/50 pointer-events-none" data-id="element-1530"></div>
                <div className="absolute top-3 right-3 w-8 h-8 border-t-2 border-r-2 border-acid-green/50 pointer-events-none" data-id="element-1531"></div>
                <div className="absolute bottom-3 left-3 w-8 h-8 border-b-2 border-l-2 border-acid-green/50 pointer-events-none" data-id="element-1532"></div>
                <div className="absolute bottom-3 right-3 w-8 h-8 border-b-2 border-r-2 border-acid-green/50 pointer-events-none" data-id="element-1533"></div>

                {/* Number watermark */}
                <span className="absolute -right-4 -bottom-8 font-bebas text-[200px] leading-none text-acid-green opacity-10 pointer-events-none select-none" data-id="element-1534">
                  {c.num}
                </span>

                {/* Top status bar */}
                <div className="absolute top-5 left-5 right-5 flex items-center justify-between z-10" data-id="element-1535">
                  <div className="inline-flex items-center gap-2 border border-acid-green/30 bg-base-black/60 backdrop-blur-sm px-2.5 py-1" data-id="element-1536">
                    <span className="w-1 h-1 bg-acid-green animate-pulse" data-id="element-1537"></span>
                    <span className="font-barlow font-bold text-[9px] text-acid-green tracking-[3px] uppercase" data-id="element-1538">
                      {c.tag}
                    </span>
                  </div>
                  <div className="font-barlow font-bold text-[10px] text-white/80 tracking-[2px]" data-id="element-1539">
                    {c.num} / 06
                  </div>
                </div>

                {/* Bottom content */}
                <div className="absolute bottom-0 left-0 right-0 z-10 p-6" data-id="element-1540">
                  <div className="text-acid-green mb-3" data-id="element-1541">
                    <c.Icon size={32} strokeWidth={1.5} data-id="element-1542" />
                  </div>
                  <h3 className="font-bebas text-3xl leading-[0.95] tracking-tight mb-3 text-white drop-shadow-lg" data-id="element-1543">
                    {c.title}
                  </h3>
                  <p className="font-barlow text-sm text-white/85 leading-snug drop-shadow-md max-w-[90%]" data-id="element-1544">
                    {c.body}
                  </p>
                </div>
              </div>)}
          </div>

          {/* Swipe hint + Animated Progress Bars */}
          <div className="flex items-center justify-between px-6 mt-4" data-id="element-1545">
            <div className="font-barlow text-[10px] text-text-dimmed tracking-[3px] uppercase" data-id="element-1546">
              {isPaused ? '← Swipe →' : 'AUTO-PLAY'}
            </div>
            <div className="flex gap-1.5" data-id="element-1547">
              {cases.map((_, i) => <button key={i} onClick={() => {
              pauseTemporarily();
              setActive(i);
            }} aria-label={`Go to mission ${i + 1}`} className={`relative h-[3px] overflow-hidden transition-all duration-300 ${i === active ? 'w-10 bg-dark-3' : 'w-4 bg-dark-3'}`} data-id="element-1548">
                  {i === active && !isPaused && <motion.span key={`progress-${active}`} initial={{
                width: '0%'
              }} animate={{
                width: '100%'
              }} transition={{
                duration: 4.5,
                ease: 'linear'
              }} className="absolute inset-y-0 left-0 bg-acid-green" data-id="element-1549" />}
                  {i === active && isPaused && <span className="absolute inset-0 bg-acid-green" data-id="element-1550" />}
                  {i < active && <span className="absolute inset-0 bg-acid-green/40" data-id="element-1551" />}
                </button>)}
            </div>
          </div>
        </div>

        {/* DESKTOP: Interactive Selector + Display */}
        <div className="hidden lg:grid lg:grid-cols-12 gap-8 items-start relative" data-id="element-1552">
          {/* Active Display Panel */}
          <div className="lg:col-span-7 lg:order-2 sticky top-32 lg:min-h-[600px]" data-id="element-1553">
            {renderVideoPanel(current, active)}
          </div>

          {/* Left: Numbered Selector */}
          <div className="lg:col-span-5 lg:order-1 relative z-10" data-id="element-1554">
            <div className="flex flex-col" data-id="element-1555">
              {cases.map((c, i) => {
              const isActive = active === i;
              return <button key={i} onClick={() => setActive(i)} onMouseEnter={() => setActive(i)} className={`group relative flex items-start gap-5 py-5 border-b border-dark-3 text-left transition-colors duration-300 ${isActive ? 'text-white' : 'text-text-dimmed hover:text-white'}`} data-id="element-1556">
                    {isActive && <motion.span layoutId="useCaseBar" className="absolute left-0 top-0 bottom-0 w-[2px] bg-acid-green" transition={{
                  type: 'spring',
                  stiffness: 400,
                  damping: 30
                }} data-id="element-1557" />}

                    <span className={`font-bebas text-2xl md:text-3xl tracking-wider w-12 pt-1 transition-colors duration-300 ${isActive ? 'text-acid-green' : 'text-text-dimmed/60'}`} data-id="element-1558">
                      {c.num}
                    </span>

                    <div className="flex items-center gap-3 flex-1" data-id="element-1559">
                      <c.Icon size={18} strokeWidth={2} className={`shrink-0 transition-colors duration-300 ${isActive ? 'text-acid-green' : 'text-text-dimmed/60'}`} data-id="element-1560" />
                      <span className="font-bebas text-xl md:text-2xl tracking-wide" data-id="element-1561">
                        {c.title}
                      </span>
                    </div>

                    <ArrowUpRight size={16} className={`shrink-0 mt-1 transition-all duration-300 ${isActive ? 'text-acid-green translate-x-0 opacity-100' : '-translate-x-2 opacity-0 group-hover:opacity-50 group-hover:translate-x-0'}`} data-id="element-1562" />
                  </button>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
};